import { useEffect, useState } from 'react';

/**
 * Hook to detect if screen is smaller than 'sm' breakpoint (640px)
 *
 * This hook provides a reactive way to detect mobile screen sizes
 * based on Tailwind's 'sm' breakpoint (640px). It automatically
 * updates when the window is resized.
 *
 * @returns {boolean} true if screen width is less than 640px, false otherwise
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isMobile = useIsMobile();
 *
 *   return (
 *     <div>
 *       {isMobile ? <MobileView /> : <DesktopView />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useIsMobile(): boolean {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 640); // 640px is Tailwind's 'sm' breakpoint
		};

		// Check on mount
		checkIsMobile();

		// Add listener for window resize
		window.addEventListener('resize', checkIsMobile);

		return () => window.removeEventListener('resize', checkIsMobile);
	}, []);

	return isMobile;
}
